import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, Check, X, UserPlus, Trophy, Flame, ArrowDownToLine, ArrowUpFromLine,
  AlertTriangle, Cpu, CheckCircle2, TrendingUp, TrendingDown, ShieldCheck, ShieldAlert, Megaphone, Gift, Gem,
} from "lucide-react";

const ICON_MAP = {
  "user-plus": UserPlus,
  "trophy": Trophy,
  "flame": Flame,
  "arrow-down-to-line": ArrowDownToLine,
  "arrow-up-from-line": ArrowUpFromLine,
  "alert-triangle": AlertTriangle,
  "cpu": Cpu,
  "check-circle-2": CheckCircle2,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  "shield-check": ShieldCheck,
  "shield-alert": ShieldAlert,
  "megaphone": Megaphone,
  "gift": Gift,
  "gem": Gem,
  "bell": Bell,
};

const KIND_COLORS = {
  direct_join: "#8AE234",
  rank_upgrade: "#EAB308",
  team_milestone: "#F97316",
  deposit_approved: "#8AE234",
  deposit_rejected: "#EF4444",
  withdrawal_approved: "#00E5FF",
  withdrawal_rejected: "#EF4444",
  bot_activated: "#A855F7",
  bot_cycle_complete: "#8AE234",
  kyc_approved: "#8AE234",
  kyc_rejected: "#EF4444",
  referral_bonus: "#8AE234",
  shareholder_commission: "#EAB308",
  shareholder_downgrade: "#F97316",
  announcement: "#00E5FF",
};

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  const pollUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  // Initial load + poll every 25s
  useEffect(() => {
    load();
    const iv = setInterval(pollUnread, 25000);
    return () => clearInterval(iv);
  }, [load, pollUnread]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load();
  };

  const markRead = async (id) => {
    setItems(items.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(Math.max(0, unread - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setItems(items.map(n => ({ ...n, read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/read-all");
      toast.success("All caught up");
    } catch { /* ignore */ }
  };

  const remove = async (id) => {
    const target = items.find(n => n.id === id);
    setItems(items.filter(n => n.id !== id));
    if (target && !target.read) setUnread(Math.max(0, unread - 1));
    try { await api.delete(`/notifications/${id}`); } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className="relative flex items-center justify-center h-9 w-9 rounded-full border border-white/10 bg-[#0F1626] hover:border-nb-cyan/40 transition"
        aria-label="Notifications"
      >
        <Bell size={16} className="text-slate-300"/>
        {unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#8AE234] text-[10px] font-bold text-black flex items-center justify-center animate-pulse">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="fixed inset-x-2 top-16 md:absolute md:top-auto md:inset-x-auto md:right-0 md:mt-2 md:w-[380px] md:max-w-[92vw] bg-[#0F1626] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-nb-cyan"/>
              <div className="font-heading text-white text-sm">Notifications</div>
              {unread > 0 && <div className="text-[10px] bg-[#8AE234]/10 text-[#8AE234] px-1.5 py-0.5 rounded-full">{unread} new</div>}
            </div>
            {items.some(n => !n.read) && (
              <button onClick={markAllRead} data-testid="notification-mark-all" className="text-[11px] text-nb-cyan hover:text-white">Mark all read</button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Bell size={28} className="mx-auto mb-2 opacity-30"/>
                Nothing here yet — your team updates will show up here.
              </div>
            ) : items.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              const color = KIND_COLORS[n.kind] || "#94A3B8";
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] cursor-pointer flex gap-3 transition ${!n.read ? "bg-nb-cyan/[0.03]" : ""}`}
                >
                  <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}40` }}>
                    <Icon size={14} style={{ color }}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm font-semibold ${n.read ? "text-slate-300" : "text-white"} truncate`}>{n.title}</div>
                      <button onClick={(e)=>{ e.stopPropagation(); remove(n.id); }} className="text-slate-500 hover:text-slate-300 shrink-0" data-testid={`notification-remove-${n.id}`}>
                        <X size={12}/>
                      </button>
                    </div>
                    <div className={`text-xs mt-0.5 ${n.read ? "text-slate-500" : "text-slate-400"} line-clamp-2`}>{n.message}</div>
                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
                      <span>{timeAgo(n.created_at)}</span>
                      {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#8AE234] inline-block"/>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
